import { useState, useCallback } from "react";

export const useClipboard = () => {
	const [copySuccess, setCopySuccess] = useState(false);
	const [isConnected, setIsConnected] = useState(false);

	const fallbackCopyText = useCallback((text: string): void => {
        // Создаем textarea
		const textArea = document.createElement("textarea");
		textArea.value = text;

        // Стили для того, чтобы элемент был "видим" для браузера, но не мешал пользователю
        // Важно: display: none или visibility: hidden могут блокировать копирование
		textArea.style.position = "fixed";
		textArea.style.left = "0";
		textArea.style.top = "0";
        textArea.style.opacity = "0";
        textArea.style.pointerEvents = "none";
        textArea.style.zIndex = "-1";
        
        // Для iOS нужно отключить редактирование, чтобы не всплывала клавиатура, но оставить возможность выделения
        textArea.contentEditable = "true";
        textArea.readOnly = false; 

		document.body.appendChild(textArea);
		
        // Выделяем текст
        textArea.focus();
		textArea.select();
        
        // Для мобильных устройств (iOS)
        textArea.setSelectionRange(0, 999999); 

		try {
			const successful = document.execCommand("copy");
			if (successful) {
				setCopySuccess(true);
				setIsConnected(true);
				setTimeout(() => setCopySuccess(false), 2000);
			} else {
				throw new Error("Fallback copy failed");
			}
		} catch (err) {
			console.error("Fallback copy failed:", err);
			showManualCopyDialog(text);
		} finally {
			document.body.removeChild(textArea);
		}
	}, []);

	const showManualCopyDialog = useCallback((text: string): void => {
		const shouldCopy = window.confirm(
			`Не удалось скопировать автоматически. \n\nНажмите OK, чтобы выделить текст, затем скопируйте его вручную.`
		);

		if (shouldCopy) {
			const tempInput = document.createElement("textarea"); // textarea лучше для многострочного текста
			tempInput.value = text;

			// Стили для видимости (модальное окно по центру)
			tempInput.style.cssText = `
				position: fixed;
				top: 50%;
				left: 50%;
				transform: translate(-50%, -50%);
				z-index: 99999;
				padding: 15px;
				font-size: 16px;
				border: 2px solid #0088cc;
				background: #ffffff;
                color: #000000;
				width: 80%;
				max-width: 300px;
                height: 100px;
                border-radius: 8px;
                box-shadow: 0 4px 20px rgba(0,0,0,0.5);
			`;

			document.body.appendChild(tempInput);
			tempInput.focus();
            tempInput.select();
			tempInput.setSelectionRange(0, 99999);

			// Флаг чтобы отслеживать было ли уже копирование
			let isCopied = false;

			// Функция для удаления input
			const removeInput = () => {
				if (document.body.contains(tempInput)) {
					document.body.removeChild(tempInput);
					// Убираем обработчики событий
					document.removeEventListener("copy", handleCopy);
                    document.removeEventListener("click", handleClickOutside);
				}
			};

			// Обработчик события копирования
			const handleCopy = (event: ClipboardEvent) => {
				if (!isCopied) {
					isCopied = true;
                    setCopySuccess(true);
                    setTimeout(() => setCopySuccess(false), 2000);
					setTimeout(removeInput, 100);
				}
			};
            
            // Закрытие при клике вне поля (если передумал)
            const handleClickOutside = (e: MouseEvent) => {
                if (e.target !== tempInput) {
                    removeInput();
                }
            };

			// Добавляем обработчики
            document.addEventListener("copy", handleCopy);
            // Небольшая задержка для клика, чтобы сразу не закрылось
            setTimeout(() => {
                document.addEventListener("click", handleClickOutside);
            }, 100);
		}
	}, []);

	const copyToClipboard = useCallback(
		async (text: string): Promise<void> => {
			try {
                // Пробуем современный API
				if (navigator.clipboard && navigator.clipboard.writeText) {
					await navigator.clipboard.writeText(text);
					setCopySuccess(true);
					setIsConnected(true);
					setTimeout(() => setCopySuccess(false), 2000);
				} else {
                    // Если не поддерживается или ошибка (например, нет https)
					fallbackCopyText(text);
				}
			} catch (error) {
				console.error("Failed to copy text via API:", error);
				fallbackCopyText(text);
			}
		},
		[fallbackCopyText]
	);

	return {
		copyToClipboard,
		copySuccess,
		isConnected,
		setCopySuccess,
		setIsConnected,
	};
};
